import { useTranslations } from '@vocab/react';
import { Card as BraidCard } from 'braid-design-system';
import { useCallback, useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';

import { Card } from 'src/components/Card/Card';
import { CardAction } from 'src/components/Card/CardAction/CardAction';
import { FormDrawer } from 'src/components/FormDrawer/FormDrawer';
import { LoadingItem } from 'src/components/LoadingBlock/LoadingBlock';
import { CREATE_RESUME_PAGE, LANGUAGES } from 'src/components/Router';
import { useId } from 'src/components/Router/useId';
import { useNavigateRoute } from 'src/components/Router/useNavigateRoute';
import { usePage } from 'src/components/Router/usePage';
import { EDIT } from 'src/consts';
import { LANGUAGES_FORM } from 'src/context/FormContext';
import { analyticsClient } from 'src/modules/analytics';
import { useTypedSelector } from 'src/modules/hooks/useTypedSelector';
import {
  getFailedActionsForParent,
  retryParent,
} from 'src/modules/redux-blend';

import { setRouteAsTouched } from '../CreateResume/actionCreators';

import translations from './.vocab';
import { EditLanguages } from './EditLanguages';
import { Languages } from './Languages';
import { useLanguages } from './data';

export const ReadLanguages = () => {
  const { t } = useTranslations(translations);
  const { languages, loading, error } = useLanguages();
  const [drawerTitle, setDrawerTitle] = useState<string>('');

  const blend = useTypedSelector((state) => state.blend);
  const page = usePage();

  const isCreateResumePage = page === 'create-resume-page';

  const isEmpty = languages.length === 0;

  const paramId = useId();
  const activeLanguage = languages.find((language) => language.id === paramId);

  useEffect(() => {
    if (activeLanguage) {
      setDrawerTitle(t('Edit language'));
    } else {
      setDrawerTitle(t('Add language'));
    }
  }, [t, activeLanguage]);

  const cardAction = (
    <CardAction
      card="languages"
      mode="EDIT"
      actionText={t('Add language')}
      data={{ automation: 'language-add' }}
      isEmpty={isEmpty}
      onClick={() => {
        analyticsClient.cardOpened({
          currentPageSection: 'language',
          currentPageSectionState: 'add',
        });

        if (page === CREATE_RESUME_PAGE) {
          setRouteAsTouched(LANGUAGES);
        }
      }}
    />
  );

  const dispatch = useDispatch();
  const navigate = useNavigateRoute();

  const editLanguage = useCallback(
    (id: string) => {
      navigate({
        card: LANGUAGES,
        mode: EDIT,
        id,
      });
      analyticsClient.cardOpened({
        currentPageSection: 'language',
        currentPageSectionState: 'edit',
      });
    },
    [navigate],
  );
  const handleRetry = useCallback(
    () => dispatch(retryParent(LANGUAGES)),
    [dispatch],
  );

  const showRetry = getFailedActionsForParent({ blend }, LANGUAGES).length > 0;

  const isLoading = Boolean(loading || error);

  return (
    <>
      <Card
        name="languages"
        title={t('Languages')}
        isEmpty={isEmpty}
        cardAction={cardAction}
        retryAction={showRetry ? handleRetry : undefined}
        emptyCardText={t(
          'Add languages to appeal to more companies and employers.',
        )}
        showOptionalBadge={isCreateResumePage}
        loading={isLoading}
        loader={
          <BraidCard>
            <LoadingItem type="text" size="standard" width="medium" />
          </BraidCard>
        }
      >
        <Languages
          displayVariant="card"
          languages={languages.map((language) => ({
            ...language,
            editLanguage,
          }))}
        />
      </Card>
      <FormDrawer
        title={drawerTitle}
        component={EditLanguages}
        card={LANGUAGES}
        formName={LANGUAGES_FORM}
      />
    </>
  );
};
